import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "../../../lib/utils";
import { bodySecondary, accentBarSection } from "../../../lib/design-system";

const StepIndicator = ({ steps, currentStep, stepProgress = {} }) => {
  const currentStepData = steps.find((s) => s.number === currentStep);

  // Calculate per-segment fill percentage
  const getSegmentFill = (stepNumber) => {
    const sp = stepProgress[stepNumber];
    if (!sp || !sp.fields || sp.fields.length === 0) {
      // Fallback: completed steps are 100%, future steps are 0%
      if (stepNumber < currentStep) return 100;
      return 0;
    }
    const done = sp.fields.filter((f) => f.done).length;
    return Math.round((done / sp.fields.length) * 100);
  };

  return (
    <div className="w-full">
      {/* ── Segmented progress bar ────────────────────────────── */}
      <div className="flex items-center gap-1">
        {steps.map((step) => {
          const fill = getSegmentFill(step.number);
          const isCompleted = step.number < currentStep;
          const isActive = step.number === currentStep;

          return (
            <div
              key={step.number}
              className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-sm overflow-hidden"
            >
              <motion.div
                className={cn(
                  "h-full rounded-sm",
                  isCompleted
                    ? "bg-emerald-500 dark:bg-emerald-400"
                    : "bg-accent1"
                )}
                initial={{ width: 0 }}
                animate={{ width: `${isCompleted ? 100 : fill}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          );
        })}
      </div>

      {/* ── Field checklist for current step ───────────────────── */}
      {stepProgress[currentStep] && stepProgress[currentStep].fields && (
        <div className="flex items-center gap-3 mt-2.5">
          {stepProgress[currentStep].fields.map((field, i) => (
            <span
              key={i}
              className={cn(
                "inline-flex items-center gap-1 text-[11px] font-medium transition-colors",
                field.done
                  ? "text-accent1"
                  : "text-gray-400 dark:text-gray-500"
              )}
            >
              {field.done ? (
                <Check className="h-3 w-3" />
              ) : (
                <span className="h-3 w-3 flex items-center justify-center">
                  <span className="block h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
                </span>
              )}
              {field.label}
            </span>
          ))}
        </div>
      )}

      {/* ── Step breadcrumb ────────────────────────────────────── */}
      <div className="flex items-center gap-1 mt-4 mb-4">
        {steps.map((step, index) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.number < currentStep;
          return (
            <React.Fragment key={step.number}>
              <span
                className={cn(
                  "text-xs font-medium transition-colors",
                  isActive
                    ? "text-accent1"
                    : isCompleted
                    ? "text-gray-700 dark:text-gray-300"
                    : "text-gray-400 dark:text-gray-500"
                )}
              >
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <span className="text-gray-300 dark:text-gray-600 text-xs mx-1">
                  /
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Active step heading ───────────────────────────────── */}
      <h2 className="text-xl sm:text-2xl font-black tracking-tight text-gray-900 dark:text-white">
        {currentStepData?.title}
      </h2>
      <p className={cn(bodySecondary, "mt-1")}>
        {currentStepData?.subtitle}
      </p>
      <div className={cn(accentBarSection, "mt-2")} />
    </div>
  );
};

export default StepIndicator;
